import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang, pick } from "../context/LanguageContext";
import { t } from "../data/translations";
import { resolveImg } from "../lib/api";
import { Menu as MenuIcon, X } from "lucide-react";

export const Navbar = ({ settings }) => {
  const { lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#menu", label: t.nav.menu },
    { href: "#story", label: t.nav.story },
    { href: "#visit", label: t.nav.visit },
  ];

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "glass" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3 group" data-testid="nav-logo">
          <span className="inline-flex items-center rounded-xl bg-white px-2.5 py-1.5 shadow-[0_4px_14px_rgba(0,0,0,0.25)] group-hover:shadow-[0_6px_20px_rgba(0,0,0,0.35)] transition-shadow duration-300">
            <img
              src={resolveImg(settings?.logo_url)}
              alt="Deli La Bendición"
              className="h-8 w-auto max-w-[150px] object-contain"
            />
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm uppercase tracking-[0.18em] text-brand-muted hover:text-brand-text transition-colors duration-300 relative after:absolute after:left-0 after:-bottom-1.5 after:h-px after:w-0 after:bg-brand-gold hover:after:w-full after:transition-[width] after:duration-300"
              data-testid={`nav-link-${l.href.slice(1)}`}
            >
              {pick(l.label, lang)}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            data-testid="bilingual-toggle"
            className="relative flex items-center h-9 rounded-full border border-white/15 text-xs font-semibold uppercase tracking-wider overflow-hidden"
          >
            <span className={`px-3 py-1.5 transition-colors duration-300 ${lang === "en" ? "text-brand-bg" : "text-brand-muted"}`}>EN</span>
            <span className={`px-3 py-1.5 transition-colors duration-300 ${lang === "es" ? "text-brand-bg" : "text-brand-muted"}`}>ES</span>
            <motion.span
              layout
              className="absolute top-1 bottom-1 w-[calc(50%-2px)] rounded-full bg-brand-gold"
              animate={{ left: lang === "en" ? 2 : "calc(50% + 0px)" }}
              transition={{ type: "spring", stiffness: 500, damping: 34 }}
            />
          </button>

          <a
            href={settings?.doordash_url || "#"}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-order-btn"
            className="hidden sm:inline-flex items-center rounded-full bg-brand-primary hover:bg-brand-primaryHover px-5 h-9 text-sm font-semibold transition-colors duration-300"
          >
            {pick(t.nav.order, lang)}
          </a>

          <button
            className="md:hidden text-brand-text"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-btn"
            aria-label="menu"
          >
            {open ? <X size={24} /> : <MenuIcon size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden glass overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="text-lg font-display" data-testid={`mobile-link-${l.href.slice(1)}`}>
                  {pick(l.label, lang)}
                </a>
              ))}
              <a href={settings?.doordash_url || "#"} target="_blank" rel="noreferrer" className="rounded-full bg-brand-primary px-5 py-3 text-center font-semibold">
                {pick(t.nav.order, lang)}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
